// render-cosmetics.ts — the "cosmetics" renderer: the Cosmetics Store (cosmetics.php).
//
// Same pipeline as the library renderer: PHP compiles the catalogue (ft_mesh_skin_store_catalog_payload →
// options/api/cosmetics_list_inc.php, also embedded as the page's first-paint JSON island); the browser renders
// it once and filters client-side by keyword + two chip rows in the page's filter panel — Type (Armor Sets /
// Weapons) and Class (Longswords, Plate Armors, …). Cards are MODEL families (colour skins as swatches), in one
// accent-headed section per class, armor first. Card markup is shared with cosmetic.php (shared/cosmetic-card).

import type { CatalogListConfig } from './types';
import { resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';
import {
    COSMETIC_CATEGORY_LABELS,
    cosmeticFamilies,
    cosmeticFamilyCardHtml,
    escapeHtml,
    wireCosmeticSwatches,
    type CosmeticFamily,
    type CosmeticProduct,
} from '../shared/cosmetic-card';

interface CosmeticsCatalogResponse {
    products: CosmeticProduct[];
    detailUrl: string;
    total: number;
}

const CATEGORY_ORDER: CosmeticProduct['category'][] = ['armor', 'weapon', 'other'];

function closePanel(panelId: string | null): void {
    if (!panelId) return;
    if (typeof window.slideToggleEl === 'function') {
        window.slideToggleEl(panelId);
        return;
    }
    const panel = document.getElementById(panelId);
    if (panel) panel.style.display = 'none';
}

function readInlinePayload(root: HTMLElement): CosmeticsCatalogResponse | null {
    const payloadId = root.getAttribute('data-ft-payload-id');
    const el = payloadId ? document.getElementById(payloadId) : null;
    if (!el?.textContent) return null;
    try {
        const parsed = JSON.parse(el.textContent);
        return parsed && Array.isArray(parsed.products) ? parsed as CosmeticsCatalogResponse : null;
    } catch (e) {
        console.error('[catalog-cosmetics] inline payload parse failed', e);
        return null;
    }
}

export function mountCatalogCosmetics(root: HTMLElement, config: CatalogListConfig): void {
    const container = results(root);
    if (!container) {
        console.error('[catalog-cosmetics] mount missing [data-ft-catalog-results]', root);
        return;
    }
    const inline = readInlinePayload(root);
    if (inline) {
        build(container, config, inline);
        return;
    }
    void (async () => {
        const loadingTimer = setTimeout(() => showLoader(root, config), 250);
        try {
            const res = await fetch(resolveUrl(config.endpoint), { credentials: 'same-origin' });
            const data = await res.json() as CosmeticsCatalogResponse;
            if (!res.ok || !Array.isArray(data.products)) throw new Error(`HTTP ${res.status}`);
            clearTimeout(loadingTimer);
            hideLoader(root);
            setStatus(root, '');
            build(container, config, data);
        } catch (e) {
            clearTimeout(loadingTimer);
            hideLoader(root);
            console.error('[catalog-cosmetics] fetch/render failed', e);
            container.innerHTML = '<div class="col-12 text-center text-muted py-4">Could not load the cosmetics. Please try again.</div>';
        }
    })();
}

/** A multi-select chip row (same look + aria-pressed contract as the library's product-type chips). */
function chipRow(host: HTMLElement | null, options: Array<{ id: string; label: string }>, onChange: () => void): () => string[] {
    if (!host) return () => [];
    host.innerHTML = '';
    const buttons = options.map(({ id, label }) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn btn-sm rounded ft-catalog-pt-chip btn-dark border d-inline-flex align-items-center';
        btn.dataset.ftChip = id;
        btn.setAttribute('aria-pressed', 'false');
        btn.textContent = label;
        btn.addEventListener('click', () => {
            const pressed = btn.getAttribute('aria-pressed') === 'true';
            btn.setAttribute('aria-pressed', String(!pressed));
            btn.classList.toggle('btn-light', !pressed);
            btn.classList.toggle('btn-dark', pressed);
            btn.classList.toggle('border', pressed);
            onChange();
        });
        host.appendChild(btn);
        return btn;
    });
    return () => buttons.filter((b) => b.getAttribute('aria-pressed') === 'true').map((b) => b.dataset.ftChip || '');
}

function build(container: HTMLElement, config: CatalogListConfig, data: CosmeticsCatalogResponse): void {
    const families = cosmeticFamilies(data.products);
    const sectionKeys: Array<{ category: CosmeticProduct['category']; group: string }> = [];
    for (const category of CATEGORY_ORDER) {
        [...new Set(families.filter((f) => f.category === category).map((f) => f.group))]
            .sort((a, b) => a.localeCompare(b))
            .forEach((group) => sectionKeys.push({ category, group }));
    }

    container.innerHTML = '';
    const built: Array<{ section: HTMLElement; cards: Array<{ el: HTMLElement; fam: CosmeticFamily; text: string }> }> = [];
    for (const { category, group } of sectionKeys) {
        const section = document.createElement('section');
        section.className = 'ft-cosmetics-section col-12';
        section.innerHTML = `<h2 class="ft-catalog-section-title">${escapeHtml(group)}
            <span class="ft-cosmetics-section__kind">${COSMETIC_CATEGORY_LABELS[category]}</span></h2>`;
        const grid = document.createElement('div');
        grid.className = 'ft-cosmetics-grid';
        section.appendChild(grid);
        container.appendChild(section);
        const cards = families.filter((f) => f.category === category && f.group === group).map((fam) => {
            const holder = document.createElement('div');
            holder.innerHTML = cosmeticFamilyCardHtml(data.detailUrl, fam).trim();
            const el = holder.firstElementChild as HTMLElement;
            grid.appendChild(el);
            const text = `${fam.family} ${fam.group} ${COSMETIC_CATEGORY_LABELS[fam.category]} ${fam.finishes.map((p) => p.finish).join(' ')}`.toLowerCase();
            return { el, fam, text };
        });
        built.push({ section, cards });
    }
    const emptyEl = document.createElement('div');
    emptyEl.className = 'col-12 text-center text-muted py-5';
    emptyEl.textContent = 'No cosmetics match your filters.';
    emptyEl.style.display = 'none';
    container.appendChild(emptyEl);
    wireCosmeticSwatches(container, data.products, data.detailUrl);

    const searchInput = config.searchInputId ? document.getElementById(config.searchInputId) as HTMLInputElement | null : null;
    let selectedTypes: () => string[] = () => [];
    let selectedClasses: () => string[] = () => [];
    const applyFilter = (): void => {
        const keyword = (searchInput?.value || '').trim().toLowerCase();
        const types = selectedTypes();
        const classes = selectedClasses();
        let total = 0;
        for (const { section, cards } of built) {
            let visible = 0;
            for (const { el, fam, text } of cards) {
                const show = (!keyword || text.includes(keyword))
                    && (types.length === 0 || types.includes(fam.category))
                    && (classes.length === 0 || classes.includes(fam.group));
                el.style.display = show ? '' : 'none';
                if (show) visible++;
            }
            section.style.display = visible ? '' : 'none';
            total += visible;
        }
        emptyEl.style.display = total ? 'none' : '';
    };

    selectedTypes = chipRow(document.querySelector('[data-ft-cosmetics-type-chips]'),
        CATEGORY_ORDER.filter((c) => families.some((f) => f.category === c)).map((c) => ({ id: c, label: COSMETIC_CATEGORY_LABELS[c] })),
        applyFilter);
    selectedClasses = chipRow(document.querySelector('[data-ft-cosmetics-class-chips]'),
        sectionKeys.map(({ group }) => ({ id: group, label: group })),
        applyFilter);

    // cosmetics.php#armor / #weapon (the product page's category crumb) pre-selects that Type chip.
    const hashType = window.location.hash.replace('#', '');
    document.querySelector<HTMLButtonElement>(`[data-ft-cosmetics-type-chips] button[data-ft-chip="${CSS.escape(hashType)}"]`)?.click();

    // Live keyword search + the house spyglass/clear trailing icons (same wiring as the library renderer).
    if (searchInput) {
        const box = searchInput.closest('.ft-inline-search-root');
        const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') ?? null;
        const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') ?? null;
        const syncTrailing = (): void => {
            if (!glyph || !clear) return;
            const hasText = (searchInput.value || '').trim().length > 0;
            glyph.classList.toggle('d-none', hasText);
            clear.classList.toggle('d-none', !hasText);
        };
        searchInput.addEventListener('input', () => { syncTrailing(); applyFilter(); });
        clear?.addEventListener('click', (e) => {
            e.preventDefault();
            searchInput.value = '';
            searchInput.focus();
            syncTrailing();
            applyFilter();
        });
        syncTrailing();
    }
    if (config.filterButtonId) {
        document.getElementById(config.filterButtonId)?.addEventListener('click', () => { closePanel(config.filterPanelId); applyFilter(); });
    }
    applyFilter();
}
